import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useOrgHelper } from "@propelauth/react";
import { Info, Loader2, Users, VideoOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../../components/ui/button";
import { Switch } from "../../../components/ui/switch";
import { Badge } from "../../../components/ui/badge";
import { Input } from "../../../components/ui/input";
import {
  useCurrentUser,
  useDeleteMeetingNoteTakerPreference,
  useMeetingNoteTakerPreference,
  useUpsertMeetingNoteTakerPreference,
  useWorkspaceMembers,
} from "../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import { cn } from "../../../lib/utils";

const SOURCES = [
  { value: "google_calendar", label: "Google Calendar" },
  { value: "zoom", label: "Zoom" },
  { value: "microsoft_teams", label: "Microsoft Teams" },
  { value: "meet", label: "Google Meet" },
];

type SettingRowProps = {
  label: string;
  description?: string;
  children: React.ReactNode;
};

const SettingRow = ({ label, description, children }: SettingRowProps) => (
  <div className="flex items-center justify-between gap-8 py-3">
    <div className="min-w-0">
      <p className="text-[13px] text-foreground">{label}</p>
      {description ? <p className="mt-0.5 text-[11px] text-muted-foreground">{description}</p> : null}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

type RecordingOptionProps = {
  title: string;
  description: string;
  badge?: string;
  selected: boolean;
  onClick: () => void;
  icon: React.ElementType;
};

const RecordingOption = ({ title, description, badge, selected, onClick, icon: Icon }: RecordingOptionProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors",
      selected
        ? "border-primary bg-primary/5"
        : "border-border hover:bg-muted",
    )}
  >
    <div
      className={cn(
        "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
        selected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
      )}
    >
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <p className={cn("text-[13px] font-medium", selected && "text-primary")}>{title}</p>
        {badge && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
            {badge}
          </Badge>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">{description}</p>
    </div>
    <div
      className={cn(
        "h-4 w-4 shrink-0 rounded-full border-2 transition-colors",
        selected ? "border-primary bg-primary" : "border-muted-foreground/40",
      )}
    >
      {selected && (
        <div className="flex h-full w-full items-center justify-center">
          <div className="h-1.5 w-1.5 rounded-full bg-white" />
        </div>
      )}
    </div>
  </button>
);

type SourceToggleProps = {
  sources: string[];
  onChange: (sources: string[]) => void;
};

const SourceToggle = ({ sources, onChange }: SourceToggleProps) => (
  <div className="flex flex-wrap gap-2">
    {SOURCES.map((source) => {
      const active = sources.includes(source.value);
      return (
        <button
          key={source.value}
          type="button"
          onClick={() => {
            onChange(
              active
                ? sources.filter((s) => s !== source.value)
                : [...sources, source.value],
            );
          }}
          className={cn(
            "rounded-md border px-3 py-1.5 text-[12px] transition-colors",
            active
              ? "border-primary bg-primary/10 text-primary"
              : "border-border text-muted-foreground hover:bg-muted",
          )}
        >
          {source.label}
        </button>
      );
    })}
  </div>
);

type NoteTakerFormProps = {
  initialData?: any;
  onSubmit: (data: any) => void;
  loading: boolean;
};

const NoteTakerForm = ({ initialData, onSubmit, loading }: NoteTakerFormProps) => {
  const [enabled, setEnabled] = useState(initialData ? (initialData.enabled ?? false) : true);
  const [minDuration, setMinDuration] = useState<string>(
    initialData?.min_meeting_duration_minutes != null
      ? String(initialData.min_meeting_duration_minutes)
      : "5",
  );
  const [allowedSources, setAllowedSources] = useState<string[]>(
    initialData?.allowed_sources ?? [],
  );

  useEffect(() => {
    if (initialData) {
      setEnabled(initialData.enabled ?? false);
      setMinDuration(
        initialData.min_meeting_duration_minutes != null
          ? String(initialData.min_meeting_duration_minutes)
          : "",
      );
      setAllowedSources(initialData.allowed_sources ?? []);
    }
  }, [initialData]);

  const handleSave = () => {
    onSubmit({
      enabled,
      min_meeting_duration_minutes: minDuration === "" ? null : Number(minDuration),
      allowed_sources: allowedSources,
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-[13px] font-medium text-foreground">Auto-record meetings</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Choose which meetings should be automatically recorded.
        </p>
        <div className="mt-3 space-y-2">
          <RecordingOption
            title="External meetings"
            description="Only meetings with external participants will be recorded"
            badge="Recommended"
            selected={enabled}
            onClick={() => setEnabled(true)}
            icon={Users}
          />
          <RecordingOption
            title="None"
            description="None of your meetings will be recorded"
            selected={!enabled}
            onClick={() => setEnabled(false)}
            icon={VideoOff}
          />
        </div>
      </div>

      {enabled && (
        <div className="space-y-1 divide-y divide-border">
          <p className="text-[13px] font-medium text-foreground pb-2">Recording Preferences</p>
          <SettingRow
            label="Minimum meeting duration"
            description="The bot will only join meetings at least this long (minutes)."
          >
            <Input
              type="number"
              min={0}
              className="h-8 w-20 text-[13px]"
              value={minDuration}
              onChange={(e) => setMinDuration(e.target.value)}
            />
          </SettingRow>
          <div className="py-3">
            <p className="text-[13px] text-foreground">Allowed sources</p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">Select calendar sources to monitor for meetings.</p>
            <div className="mt-2">
              <SourceToggle sources={allowedSources} onChange={setAllowedSources} />
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <Button size="sm" className="h-8 text-[12px]" onClick={handleSave} disabled={loading}>
          {loading && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
          Save Changes
        </Button>
      </div>
    </div>
  );
};

const WorkspaceSettingsPanel = () => {
  const workspace: any = useWorkspaceStore((state) => state.workspace);
  const { data: pref, isLoading } = useMeetingNoteTakerPreference({
    workspaceId: workspace?.id,
  });
  const upsertMutation = useUpsertMeetingNoteTakerPreference();
  const queryClient = useQueryClient();

  const handleSave = async (data: any) => {
    try {
      await upsertMutation.mutateAsync({
        workspace_id: workspace.id,
        workspace_member_id: null,
        data: { ...data, provider: "recall_ai" },
      });
      toast.success("Workspace defaults updated.");
      queryClient.invalidateQueries(["meeting-note-taker-preference", workspace.id]);
    } catch {
      toast.error("Failed to save workspace settings.");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <NoteTakerForm initialData={pref} onSubmit={handleSave} loading={upsertMutation.isLoading} />;
};

const MemberSettingsPanel = () => {
  const workspace: any = useWorkspaceStore((state) => state.workspace);
  const { data: user }: any = useCurrentUser({});
  const { data: workspaceMembers }: any = useWorkspaceMembers({ workspaceId: workspace?.id });

  const currentMember = workspaceMembers?.members?.find((m: any) => m.email === user?.email)
    || workspaceMembers?.users?.find((u: any) => u.email === user?.email);

  const { data: memberPref, isLoading: memberLoading } = useMeetingNoteTakerPreference({
    workspaceId: workspace?.id,
    workspaceMemberId: currentMember?.id,
  });

  const { data: workspacePref } = useMeetingNoteTakerPreference({
    workspaceId: workspace?.id,
  });

  const upsertMutation = useUpsertMeetingNoteTakerPreference();
  const deleteMutation = useDeleteMeetingNoteTakerPreference();
  const queryClient = useQueryClient();

  const [localOverride, setLocalOverride] = useState(false);
  const isCustomizing = !!memberPref || localOverride;

  const handleCustomizeToggle = async (checked: boolean) => {
    if (!currentMember?.id) return;

    if (checked) {
      setLocalOverride(true);
    } else {
      setLocalOverride(false);
      if (memberPref) {
        try {
          await deleteMutation.mutateAsync({
            workspace_id: workspace.id,
            workspace_member_id: currentMember.id,
          });
          toast.success("Reverted to workspace defaults.");
          queryClient.setQueryData(["meeting-note-taker-preference", workspace.id, currentMember.id], null);
          queryClient.invalidateQueries(["meeting-note-taker-preference", workspace.id]);
        } catch {
          toast.error("Failed to revert settings.");
        }
      }
    }
  };

  const handleSave = async (data: any) => {
    if (!currentMember?.id) return;
    try {
      const result = await upsertMutation.mutateAsync({
        workspace_id: workspace.id,
        workspace_member_id: currentMember.id,
        data: { ...data, provider: "recall_ai" },
      });
      toast.success("Your settings have been updated.");
      queryClient.setQueryData(["meeting-note-taker-preference", workspace.id, currentMember.id], result.data);
      queryClient.invalidateQueries(["meeting-note-taker-preference", workspace.id]);
    } catch {
      toast.error("Failed to save settings.");
    }
  };

  if (memberLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!currentMember) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
        <p className="text-[13px] text-destructive">Could not identify your workspace member profile.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SettingRow
        label="Customize personal settings"
        description="Override workspace defaults with your own preferences."
      >
        <Switch
          checked={isCustomizing}
          onCheckedChange={handleCustomizeToggle}
        />
      </SettingRow>

      {!isCustomizing ? (
        <div className="flex items-start gap-2.5 rounded-lg border border-border bg-muted/50 px-4 py-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <div>
            <p className="text-[13px] font-medium text-foreground">Using workspace defaults</p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">You are currently inheriting settings from the workspace.</p>
            {workspacePref && (workspacePref as any).enabled != null ? (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Badge variant={(workspacePref as any).enabled ? "default" : "secondary"} className="text-[10px]">
                  {(workspacePref as any).enabled ? "Recording Enabled" : "Recording Disabled"}
                </Badge>
                {(workspacePref as any).enabled && (
                  <>
                    {(workspacePref as any).min_meeting_duration_minutes != null && (
                      <span className="text-[11px] text-muted-foreground">
                        Min: {(workspacePref as any).min_meeting_duration_minutes}m
                      </span>
                    )}
                    {(workspacePref as any).allowed_sources?.length > 0 && (
                      <span className="text-[11px] text-muted-foreground">
                        Sources: {(workspacePref as any).allowed_sources.join(", ")}
                      </span>
                    )}
                  </>
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="border-t border-border pt-4">
          <NoteTakerForm
            initialData={memberPref || workspacePref}
            onSubmit={handleSave}
            loading={upsertMutation.isLoading || deleteMutation.isLoading}
          />
        </div>
      )}
    </div>
  );
};

const MeetingNoteTakerPage = () => {
  const { orgHelper } = useOrgHelper();
  const workspace = useWorkspaceStore((state) => state.workspace);

  const userRole = useMemo(() => {
    const uniqueIdentifier = workspace?.unique_identifier;
    if (!uniqueIdentifier) return undefined;
    const orgs: any[] = orgHelper?.getOrgs?.() ?? [];
    const match = orgs.find((org: any) => org?.orgMetadata?.unique_identifier === uniqueIdentifier);
    return match?.userAssignedRole as string | undefined;
  }, [orgHelper, workspace?.unique_identifier]);

  const canManageWorkspace = userRole === "Owner" || userRole === "Admin";
  const [activeTab, setActiveTab] = useState<"member" | "workspace">("member");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-medium text-foreground">Call Intelligence</h1>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Automatically join and transcribe your meetings
        </p>
      </div>

      <div className="flex gap-1 border-b border-border">
        <button
          type="button"
          onClick={() => setActiveTab("member")}
          className={cn(
            "px-3 py-2 text-[13px] font-medium transition-colors border-b-2 -mb-px",
            activeTab === "member"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground",
          )}
        >
          My Settings
        </button>
        {canManageWorkspace && (
          <button
            type="button"
            onClick={() => setActiveTab("workspace")}
            className={cn(
              "px-3 py-2 text-[13px] font-medium transition-colors border-b-2 -mb-px",
              activeTab === "workspace"
                ? "border-primary text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            Workspace Settings
          </button>
        )}
      </div>

      {activeTab === "member" ? <MemberSettingsPanel /> : <WorkspaceSettingsPanel />}
    </div>
  );
};

export default MeetingNoteTakerPage;
